/**
 * Semantics of Wenco CSV column "Umsatz" (VisiTour / Gebietsplanung export).
 *
 * Research (2026-05, internal Wenco CSV + workshop notes):
 * - Values are small integers (typ. 9–170, median ~26), not visit revenue in €.
 * - Strong correlation with shelf meters and inverse visit rhythm (weeks).
 * - Stable product `umsatz * rhythmus` (~76 T€) → weekly planning index.
 * - VisiTour shows "Umsatz" beside "Potential" for territory balancing; Wenco uses
 *   it with visit duration for "Umsatz pro 10 Minuten" (salary / workload planning).
 * - Real accounting revenue lives in DeltaMaster (monthly, per customer); this field
 *   is the operational planning figure from ERP master data export.
 *
 * Conclusion for the demo: store and display as **T€ per week** (thousand EUR/week).
 */
import wencoDemoModeConfig from "./wencoDemoModeConfig.json";

export const WENCO_UMSATZ_UNIT = "teur_per_week";

const eurMultiplier =
  Number(wencoDemoModeConfig?.umsatz?.eurMultiplier) || 1000;

export const getWencoUmsatzConfig = () => ({
  unit: wencoDemoModeConfig?.umsatz?.unit || WENCO_UMSATZ_UNIT,
  eurMultiplier,
  sourceColumn: wencoDemoModeConfig?.umsatz?.sourceColumn || "Umsatz",
  showInWorkbookHeader:
    wencoDemoModeConfig?.umsatz?.showInWorkbookHeader === true,
  workbookLabel:
    wencoDemoModeConfig?.umsatz?.workbookLabel ||
    "Geplanter Wochenumsatz",
  workbookHint:
    wencoDemoModeConfig?.umsatz?.workbookHint ||
    "Summe der Wochenumsätze (T€) aller Kunden in dieser Arbeitsmappe laut Kunden.csv / VisiTour."
});

/** Raw CSV value → T€/week (invalid → 0). */
export const parseWencoUmsatzTeur = value => {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : 0;
};

/** T€/week → EUR/week for comparisons or future charts. */
export const wencoUmsatzTeurToEur = teur => parseWencoUmsatzTeur(teur) * eurMultiplier;

export const sumWencoUmsatzTeur = values =>
  values.reduce((sum, value) => sum + parseWencoUmsatzTeur(value), 0);

const teurFormatter = new Intl.NumberFormat("de-DE", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 1
});

/** e.g. "123,5 T€/Woche" */
export const formatWencoUmsatzTeurPerWeek = teur => {
  const amount = parseWencoUmsatzTeur(teur);
  if (amount <= 0) return "";
  return `${teurFormatter.format(amount)} T€/Woche`;
};

/** Workbook header: sum of customer weekly planning revenues. */
export const formatWencoWorkbookRevenue = totalTeur =>
  formatWencoUmsatzTeurPerWeek(totalTeur);
